import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["exam", "subject", "status"];

const MasterData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("exam");
  const [newValue, setNewValue] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/masterdata`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to fetch master data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newValue.trim()) {
      toast.error("Please enter a value");
      return;
    }

    try {
      await axios.post(`${API}/masterdata`, {
        category: selectedCategory,
        value: newValue.trim()
      });
      toast.success("Master data added");
      setShowModal(false);
      setNewValue("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add master data");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/masterdata/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const groupedData = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = data.filter(d => d.category === cat);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="masterdata-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Master Data</h1>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
          data-testid="add-masterdata-btn"
        >
          <Plus size={16} /> Add Entry
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {CATEGORIES.map((category) => (
          <div key={category} className="stat-card">
            <h2 className="text-lg font-semibold text-gray-900 capitalize mb-4">
              {category}s
            </h2>
            {groupedData[category].length > 0 ? (
              <div className="space-y-2">
                {groupedData[category].map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 rounded bg-gray-50"
                  >
                    <span className="text-sm">{item.value}</span>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="btn-danger p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No entries</p>
            )}
          </div>
        ))}
      </div>

      {/* Add Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Master Data</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Value</Label>
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Enter value"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit">Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterData;
